import { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';
import { DateTimePicker } from './DateTimePicker';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { getEvents, createEvent, updateEvent, deleteEvent, getEventLeads, getGroups, assignGroupToEvent, updateLead, markAttendance, getActivities, getLeads, confirmFromWaitingList } from '../services/api';

export const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'all', 'upcoming', 'running', 'past'
  const [leadsModalOpen, setLeadsModalOpen] = useState(false);
  const [selectedEventLeads, setSelectedEventLeads] = useState([]);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [groupAssignModalOpen, setGroupAssignModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [healthDeclarations, setHealthDeclarations] = useState({});
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [addLeadModalOpen, setAddLeadModalOpen] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [loadingAvailableLeads, setLoadingAvailableLeads] = useState(false);
  const [selectedLeadToAdd, setSelectedLeadToAdd] = useState('');
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [phoneMenuOpen, setPhoneMenuOpen] = useState(null);
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    event_name: '',
    event_date: '',
    event_end_date: '',
    activity_id: '',
    capacity: 0,
    is_private: false,
    recurrence_type: 'none',
    recurrence_end_date: '',
  });
  const toast = useToast();

  useEffect(() => {
    fetchEvents();
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const data = await getActivities(true); // Only active activities
      setActivities(data);
    } catch (error) {
      console.error('Failed to load activities:', error);
      toast.error(t('failedToLoadActivities') || 'Failed to load activities');
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const filters = {};
      const data = await getEvents(filters);

      // Sort all events by start date (closest to farthest)
      const sortedEvents = data.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
      setEvents(sortedEvents);
    } catch (error) {
      toast.error(t('failedToLoadEvents') || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // Determine event status based on start and end dates
  const getEventStatus = (event) => {
    const now = new Date();
    const startDate = new Date(event.event_date);
    const endDate = new Date(event.event_end_date);

    // Check if dates are valid
    if (isNaN(startDate.getTime())) {
      console.warn('Invalid start date for event:', event);
      return 'upcoming';
    }

    // If no end date or invalid end date, use start date + 1 hour as default
    if (!event.event_end_date || isNaN(endDate.getTime())) {
      const defaultEndDate = new Date(startDate.getTime() + 60 * 60 * 1000);
      if (now < startDate) {
        return 'upcoming';
      } else if (now >= startDate && now <= defaultEndDate) {
        return 'running';
      } else {
        return 'past';
      }
    }

    if (now < startDate) {
      return 'upcoming'; // Event hasn't started yet
    } else if (now >= startDate && now <= endDate) {
      return 'running'; // Event is currently happening
    } else {
      return 'past'; // Event has ended
    }
  };

  const handleOpenModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      // DateTimePicker expects ISO format (UTC), so pass the dates as-is
      setFormData({
        event_name: event.event_name,
        event_date: event.event_date, // Keep ISO format
        event_end_date: event.event_end_date, // Keep ISO format
        activity_id: event.activity_id || '',
        capacity: event.capacity || 0,
        is_private: event.is_private || false,
        recurrence_type: event.recurrence_type || 'none',
        recurrence_end_date: event.recurrence_end_date || '',
      });
    } else {
      setEditingEvent(null);
      // Set default date/time to current time when creating new event
      const now = new Date();
      // Default end time is 1.5 hours after start
      const endTime = new Date(now.getTime() + 90 * 60 * 1000);

      setFormData({
        event_name: '',
        event_date: now.toISOString(), // ISO format for DateTimePicker
        event_end_date: endTime.toISOString(), // ISO format for DateTimePicker
        activity_id: '',
        capacity: 0,
        is_private: false,
        recurrence_type: 'none',
        recurrence_end_date: '',
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingEvent(null);
    setFormData({
      event_name: '',
      event_date: '',
      event_end_date: '',
      activity_id: '',
      capacity: 0,
      is_private: false,
      recurrence_type: 'none',
      recurrence_end_date: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Convert datetime-local to ISO format and activity_id to integer
      const submitData = {
        ...formData,
        event_date: new Date(formData.event_date).toISOString(),
        event_end_date: formData.event_end_date ? new Date(formData.event_end_date).toISOString() : new Date(new Date(formData.event_date).getTime() + 60 * 60 * 1000).toISOString(),
        activity_id: formData.activity_id ? parseInt(formData.activity_id) : null,
        recurrence_type: formData.recurrence_type || 'none',
        recurrence_end_date: formData.recurrence_end_date || null,
      };

      console.log('Submitting event data:', submitData);

      if (editingEvent) {
        await updateEvent(editingEvent.id, submitData);
        toast.success(t('eventUpdated') || 'Event updated successfully');
      } else {
        await createEvent(submitData);
        toast.success(t('eventCreated') || 'Event created successfully');
      }
      handleCloseModal();
      fetchEvents();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(t('failedToSaveEvent') || 'Failed to save event');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t('deleteEventConfirm') || 'Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await deleteEvent(id);
      toast.success(t('eventDeleted') || 'Event deleted successfully');
      fetchEvents();
    } catch (error) {
      const errorMsg = error.response?.data?.error?.code === 'EVENT_HAS_LEADS'
        ? (t('cannotDeleteEventWithLeads') || 'Cannot delete event with assigned leads')
        : (t('eventDeleteFailed') || 'Failed to delete event');
      toast.error(errorMsg);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value,
    }));
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const locale = language === 'he' ? 'he-IL' : 'en-US';
    
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  // Filter events based on active tab
  const getFilteredEvents = () => {
    if (activeTab === 'all') {
      return events;
    }
    return events.filter(event => getEventStatus(event) === activeTab);
  };

  const handleViewLeads = async (event) => {
    try {
      setLoadingLeads(true);
      setSelectedEvent(event);
      setSelectedEventName(event.event_name);
      const leadsData = await getEventLeads(event.id);
      const leads = Array.isArray(leadsData) ? leadsData : [];
      setSelectedEventLeads(leads);

      // Initialize health declarations and attendance status from lead data
      const initialHealthDeclarations = {};
      const initialAttendanceStatus = {};
      leads.forEach(lead => {
        // Always initialize health declaration (even if null/false)
        initialHealthDeclarations[lead.id] = lead.health_declaration === true;

        // Always initialize attendance status (even if null/undefined)
        if (lead.attendance_status) {
          initialAttendanceStatus[lead.id] = lead.attendance_status;
        }
      });

      // Set state with initialized values
      setHealthDeclarations(initialHealthDeclarations);
      setAttendanceStatus(initialAttendanceStatus);

      setLeadsModalOpen(true);
    } catch (error) {
      console.error('Failed to load event leads:', error);
      toast.error(t('failedToLoadLeads') || 'Failed to load event leads');
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleUnassignLead = async (leadId) => {
    if (!confirm(t('confirmUnassignLead') || 'Are you sure you want to unassign this lead from the event?')) {
      return;
    }

    try {
      // Unassign by setting event_id and action_date to null
      // Pass the current event_id so backend knows which attendance record to delete
      await updateLead(leadId, {
        event_id: null,
        action_date: null,
        _remove_from_event_id: selectedEvent?.id // Pass event ID to delete specific attendance record
      });

      toast.success(t('leadUnassigned') || 'Lead unassigned from event successfully');

      // Refresh the leads list
      if (selectedEvent) {
        const leadsData = await getEventLeads(selectedEvent.id);
        setSelectedEventLeads(Array.isArray(leadsData) ? leadsData : []);

        // Refresh events list to update assigned count
        fetchEvents();
      }
    } catch (error) {
      console.error('Failed to unassign lead:', error);
      
      // Check for specific error about attended leads
      if (error.response?.data?.message?.includes('attended')) {
        toast.error(t('cannotUnassignAttendedLead') || 'Cannot unassign lead who attended the event. Mark as absent first.');
      } else {
        toast.error(t('failedToUnassignLead') || 'Failed to unassign lead from event');
      }
    }
  };

  const handleConfirmFromWaitingList = async (leadId) => {
    if (!selectedEvent?.id) return;

    try {
      await confirmFromWaitingList(leadId, selectedEvent.id);
      toast.success(t('leadConfirmedFromWaitingList') || 'Lead confirmed from waiting list successfully');

      // Refresh the leads list
      const leadsData = await getEventLeads(selectedEvent.id);
      setSelectedEventLeads(Array.isArray(leadsData) ? leadsData : []);

      // Refresh events list to update counts
      fetchEvents();
    } catch (error) {
      console.error('Failed to confirm lead from waiting list:', error);

      if (error.response?.data?.error?.code === 'EVENT_AT_CAPACITY') {
        toast.error(t('eventAtCapacity') || 'Event has reached capacity');
      } else {
        toast.error(t('failedToConfirmLead') || 'Failed to confirm lead from waiting list');
      }
    }
  };

  const handleAttendanceChange = async (leadId, attendance) => {
    try {
      // Get health declaration status for this lead
      const healthDeclaration = healthDeclarations[leadId] || null;

      // Update local state immediately for better UX
      setAttendanceStatus(prev => ({
        ...prev,
        [leadId]: attendance
      }));

      // Use the new attendance API endpoint, passing the current event ID context
      await markAttendance(leadId, attendance, healthDeclaration, selectedEvent?.id);

      toast.success(t('attendanceUpdated') || 'Attendance updated successfully');

      // Don't refresh the leads list - it clears our local state
      // The local state update above is sufficient for UI feedback
    } catch (error) {
      console.error('Failed to update attendance:', error);
      toast.error(t('failedToUpdateAttendance') || 'Failed to update attendance');

      // Revert local state on error
      setAttendanceStatus(prev => {
        const newState = { ...prev };
        delete newState[leadId];
        return newState;
      });
    }
  };

  const handleHealthDeclarationChange = async (leadId, hasDeclaration) => {
    try {
      // Update local state immediately
      const newValue = hasDeclaration ? true : null;
      setHealthDeclarations(prev => ({
        ...prev,
        [leadId]: newValue
      }));

      // Check if attendance has been marked for this lead
      const currentAttendance = attendanceStatus[leadId];

      // Always use markAttendance API - it now supports updating health declaration alone
      // If attendance is marked, pass it; if not, pass null and the API will only update health declaration
      await markAttendance(leadId, currentAttendance || null, newValue, selectedEvent?.id);
      toast.success(t('healthDeclarationUpdated') || 'Health declaration updated successfully');
    } catch (error) {
      console.error('Failed to update health declaration:', error);
      toast.error(t('failedToUpdateHealthDeclaration') || 'Failed to update health declaration');

      // Revert local state on error
      setHealthDeclarations(prev => ({
        ...prev,
        [leadId]: !hasDeclaration ? true : null
      }));
    }
  };

  const handleCloseLeadsModal = () => {
    setLeadsModalOpen(false);
    setSelectedEventLeads([]);
    setSelectedEventName('');
    setSelectedEvent(null);
    setHealthDeclarations({});
    setAttendanceStatus({});
  };

  const handleCopyParticipantsList = () => {
    if (!selectedEvent || selectedEventLeads.length === 0) {
      toast.error(t('noLeadsToC opy') || 'No leads to copy');
      return;
    }

    // Format event time in Hebrew format
    const eventDate = new Date(selectedEvent.event_date);
    const hours = String(eventDate.getHours()).padStart(2, '0');
    const minutes = String(eventDate.getMinutes()).padStart(2, '0');
    const timeString = `שעה ${hours}:${minutes}:`;

    // Build the participants list
    const participantsList = selectedEventLeads
      .map(lead => `* ${lead.field_data?.full_name || 'Unnamed Lead'}`)
      .join('\n');

    // Combine time and list
    const fullText = `${timeString}\n${participantsList}`;

    // Copy to clipboard
    navigator.clipboard.writeText(fullText).then(() => {
      toast.success(t('copiedToClipboard') || 'Copied to clipboard');
    }).catch((err) => {
      console.error('Failed to copy:', err);
      toast.error(t('failedToCopy') || 'Failed to copy to clipboard');
    });
  };

  const handleOpenGroupAssignModal = async (event) => {
    setSelectedEvent(event);
    setSelectedGroupId('');
    try {
      const groupsData = await getGroups(true); // Only active groups
      setGroups(groupsData);
      setGroupAssignModalOpen(true);
    } catch (error) {
      console.error('Failed to load groups:', error);
      toast.error(t('failedToLoadGroups') || 'Failed to load groups');
    }
  };

  const handleCloseGroupAssignModal = () => {
    setGroupAssignModalOpen(false);
    setSelectedEvent(null);
    setSelectedGroupId('');
    setGroups([]);
  };

  const handleAssignGroup = async () => {
    if (!selectedGroupId || !selectedEvent) {
      toast.error(t('pleaseSelectGroup') || 'Please select a group');
      return;
    }

    try {
      await assignGroupToEvent(parseInt(selectedGroupId, 10), selectedEvent.id);
      toast.success(t('groupAssignedToEvent') || 'Group assigned to event successfully');
      handleCloseGroupAssignModal();
      fetchEvents(); // Refresh events to update assigned count
    } catch (error) {
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'EVENT_CAPACITY_EXCEEDED') {
        toast.error(t('eventCapacityExceeded') || 'Event capacity would be exceeded');
      } else if (errorCode === 'GROUP_NOT_FOUND') {
        toast.error(t('groupNotFound') || 'Group not found');
      } else if (errorCode === 'EVENT_NOT_FOUND') {
        toast.error(t('eventNotFound') || 'Event not found');
      } else {
        toast.error(t('failedToAssignGroup') || 'Failed to assign group to event');
      }
    }
  };

  const handleOpenAddLeadModal = async () => {
    setAddLeadModalOpen(true);
    setLoadingAvailableLeads(true);
    try {
      // Fetch all leads
      const response = await getLeads({ limit: 1000 });
      setAvailableLeads(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load leads:', error);
      toast.error(t('failedToLoadLeads') || 'Failed to load leads');
      setAvailableLeads([]);
    } finally {
      setLoadingAvailableLeads(false);
    }
  };

  const handleCloseAddLeadModal = () => {
    setAddLeadModalOpen(false);
    setSelectedLeadToAdd('');
    setLeadSearchQuery('');
    setAvailableLeads([]);
  };

  const handleAddLeadToEvent = async () => {
    if (!selectedLeadToAdd || !selectedEvent) {
      toast.error(t('pleaseSelectLead') || 'Please select a lead');
      return;
    }

    try {
      // Update the lead to assign it to the event
      await updateLead(selectedLeadToAdd, {
        event_id: selectedEvent.id,
        action_date: selectedEvent.event_date
      });

      toast.success(t('leadAddedToEvent') || 'Lead added to event successfully');
      handleCloseAddLeadModal();

      // Refresh the leads list for the current event
      if (selectedEvent) {
        const leadsData = await getEventLeads(selectedEvent.id);
        setSelectedEventLeads(Array.isArray(leadsData) ? leadsData : []);
      }

      // Refresh events list to update assigned count
      fetchEvents();
    } catch (error) {
      console.error('Failed to add lead to event:', error);
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'EVENT_AT_CAPACITY') {
        toast.error(t('eventAtCapacity') || 'Event is at full capacity');
      } else if (errorCode === 'LEAD_NOT_FOUND') {
        toast.error(t('leadNotFound') || 'Lead not found');
      } else if (errorCode === 'EVENT_NOT_FOUND') {
        toast.error(t('eventNotFound') || 'Event not found');
      } else {
        toast.error(t('failedToAddLead') || 'Failed to add lead to event');
      }
    }
  };

  const handlePhoneClick = (leadId, phone) => {
    if (!phone) return;
    setPhoneMenuOpen(phoneMenuOpen === leadId ? null : leadId);
  };

  const handleCopyPhone = (phone) => {
    if (!phone) return;
    navigator.clipboard.writeText(phone).then(() => {
      toast.success(t('copiedToClipboard') || 'Copied to clipboard');
      setPhoneMenuOpen(null);
    }).catch((err) => {
      console.error('Failed to copy:', err);
      toast.error(t('failedToCopy') || 'Failed to copy to clipboard');
    });
  };

  const handleOpenWhatsApp = (phone) => {
    if (!phone) return;
    // Remove any non-digit characters except +
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
    setPhoneMenuOpen(null);
  };

  if (loading) {
    return <div className="text-center py-8">{t('loading')}</div>;
  }

  return (
    <div>
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('events') || 'Events'}</h2>
            <p className="text-gray-600 mt-1">
              {t('manageEvents') || 'Manage trial sessions, workshops, and other events'}
            </p>
          </div>
          <Button onClick={() => handleOpenModal()} className="w-full sm:w-auto">
            <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">{t('addEvent') || 'Add Event'}</span>
            <span className="sm:hidden">{t('add') || 'Add'}</span>
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'upcoming'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('upcoming') || 'Upcoming'} ({events.filter(e => getEventStatus(e) === 'upcoming').length})
          </button>
          <button
            onClick={() => setActiveTab('running')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'running'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('running') || 'Running'} ({events.filter(e => getEventStatus(e) === 'running').length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'past'
                ? 'bg-gray-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('past') || 'Past'} ({events.filter(e => getEventStatus(e) === 'past').length})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'all'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('all') || 'All'} ({events.length})
          </button>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {t('eventName') || 'Event Name'}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {t('eventDate') || 'Date'}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {t('activityCategory') || 'Category'}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {t('visibility') || 'Visibility'}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {t('capacity') || 'Capacity'}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {t('assigned') || 'Assigned'}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getFilteredEvents().length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    {t('noEvents') || 'No events found'}
                  </td>
                </tr>
              ) : (
                (() => {
                  const filteredEvents = getFilteredEvents();

                  const renderEvent = (event) => {
                    const eventStatus = getEventStatus(event);
                    const assignedCount = parseInt(event.assigned_count) || 0;
                    const waitingListCount = parseInt(event.waiting_list_count) || 0;
                    const capacity = event.capacity || 0;
                    const isFull = capacity > 0 && assignedCount >= capacity;

                    return (
                      <tr key={event.id} className={eventStatus === 'past' ? 'bg-gray-50' : eventStatus === 'running' ? 'bg-green-50' : ''}>
                        <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">{event.event_name}</div>
                            {eventStatus === 'running' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500 text-white animate-pulse">
                                ● {t('live') || 'LIVE'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`px-6 py-4 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <div className="text-sm text-gray-900">{formatDateTime(event.event_date)}</div>
                          {event.event_end_date && (
                            <div className="text-xs text-gray-500">
                              {t('to') || 'to'} {formatDateTime(event.event_end_date)}
                            </div>
                          )}
                          {eventStatus === 'past' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800 mt-1">
                              {t('past') || 'Past'}
                            </span>
                          )}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {event.activity_id ? (activities.find(a => a.id === event.activity_id)?.activity_name || '-') : '-'}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {event.is_private ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              🔒 {t('private') || 'Private'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              🌐 {t('public') || 'Public'}
                            </span>
                          )}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {capacity === 0 ? (t('unlimited') || 'Unlimited') : capacity}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <div className={`flex flex-col gap-1 ${language === 'he' ? 'items-end' : 'items-start'}`}>
                            <div className={`flex items-center gap-2 ${language === 'he' ? 'flex-row-reverse' : ''}`}>
                              <span className="text-sm text-gray-900">{assignedCount}</span>
                              {isFull && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  {t('full') || 'Full'}
                                </span>
                              )}
                            </div>
                            {waitingListCount > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                {t('waitingList')}: {waitingListCount}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <div className={`flex items-center gap-2 ${language === 'he' ? 'justify-end' : 'justify-start'}`}>
                            <button
                              onClick={() => handleOpenGroupAssignModal(event)}
                              className="text-purple-600 hover:text-purple-900"
                              title={t('assignGroup') || 'Assign Group'}
                            >
                              <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleViewLeads(event)}
                              className="text-green-600 hover:text-green-900"
                              title={t('viewAssignedLeads') || 'View Assigned Leads'}
                            >
                              <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleOpenModal(event)}
                              className="text-blue-600 hover:text-blue-900"
                              title={t('edit') || 'Edit'}
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(event.id)}
                              className="text-red-600 hover:text-red-900"
                              title={t('delete') || 'Delete'}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  };

                  return filteredEvents.map(event => renderEvent(event));
                })()
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {getFilteredEvents().length === 0 ? (
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-500">
            {t('noEvents') || 'No events found'}
          </div>
        ) : (
          getFilteredEvents().map((event) => {
            const eventStatus = getEventStatus(event);
            const assignedCount = parseInt(event.assigned_count) || 0;
            const waitingListCount = parseInt(event.waiting_list_count) || 0;
            const capacity = event.capacity || 0;
            const isFull = capacity > 0 && assignedCount >= capacity;

            return (
              <div key={event.id} className={`bg-white shadow-md rounded-lg p-4 ${
                eventStatus === 'past' ? 'bg-gray-50' : eventStatus === 'running' ? 'bg-green-50 border-2 border-green-500' : ''
              }`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 justify-end">
                      <h3 className="text-lg font-semibold text-gray-900 text-right">{event.event_name}</h3>
                      {eventStatus === 'running' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500 text-white animate-pulse">
                          ● {t('live') || 'LIVE'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {event.is_private ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        🔒 {t('private') || 'Private'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        🌐 {t('public') || 'Public'}
                      </span>
                    )}
                    {eventStatus === 'past' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800">
                        {t('past') || 'Past'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                  <div className="text-right">
                    <span className="text-gray-500">{t('eventDate') || 'Date'}:</span>
                    <p className="font-medium">{formatDateTime(event.event_date)}</p>
                    {event.event_end_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t('to') || 'to'} {formatDateTime(event.event_end_date)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-gray-500">{t('activityCategory') || 'Category'}:</span>
                    <p className="font-medium">
                      {event.activity_id ? (activities.find(a => a.id === event.activity_id)?.activity_name || '-') : '-'}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-500">{t('capacity') || 'Capacity'}:</span>
                    <p className="font-medium">
                      {capacity === 0 ? (t('unlimited') || 'Unlimited') : capacity}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{t('assigned') || 'Assigned'}:</span>
                      <span className="text-sm font-medium">{assignedCount}</span>
                      {isFull && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          {t('full') || 'Full'}
                        </span>
                      )}
                    </div>
                    {waitingListCount > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                        {t('waitingList')}: {waitingListCount}
                      </span>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenGroupAssignModal(event)}
                      className="p-2 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded"
                      title={t('assignGroup') || 'Assign Group'}
                    >
                      <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleViewLeads(event)}
                      className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded"
                      title={t('viewAssignedLeads') || 'View Assigned Leads'}
                    >
                      <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleOpenModal(event)}
                      className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded"
                      title={t('edit') || 'Edit'}
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(event.id)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded"
                      title={t('delete') || 'Delete'}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingEvent ? (t('editEvent') || 'Edit Event') : (t('addEvent') || 'Add Event')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={t('eventName') || 'Event Name'}
            name="event_name"
            value={formData.event_name}
            onChange={handleChange}
            required
          />

          <DateTimePicker
            label={t('eventStartDate') || 'Start Date & Time'}
            name="event_date"
            value={formData.event_date}
            onChange={handleChange}
            required
          />

          <DateTimePicker
            label={t('eventEndDate') || 'End Date & Time'}
            name="event_end_date"
            value={formData.event_end_date}
            onChange={handleChange}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('activityCategory') || 'Activity Category'}
            </label>
            <select
              name="activity_id"
              value={formData.activity_id}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('selectActivity') || 'Select Activity...'}</option>
              {activities
                .filter(a => !a.parent_activity_id)
                .map((parentActivity) => [
                  <option key={parentActivity.id} value={parentActivity.id}>
                    {parentActivity.activity_name}
                  </option>,
                  ...activities
                    .filter(a => a.parent_activity_id === parentActivity.id)
                    .map((subActivity) => (
                      <option key={subActivity.id} value={subActivity.id}>
                        {'  └─ ' + subActivity.activity_name}
                      </option>
                    ))
                ]).flat()}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('capacity') || 'Capacity'}
            </label>
            <input
              type="number"
              name="capacity"
              value={formData.capacity}
              onChange={handleChange}
              min="0"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('capacityHelp') || '0 = unlimited capacity'}
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_private"
              id="is_private"
              checked={formData.is_private}
              onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_private" className="ml-2 block text-sm text-gray-700">
              {t('privateEvent') || 'Private Event'}
            </label>
          </div>

          {/* Recurrence Settings */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('recurrence') || 'Recurrence'}
            </h3>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('recurrenceType') || 'Repeat'}
              </label>
              <select
                name="recurrence_type"
                value={formData.recurrence_type}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="none">{t('noRecurrence') || 'Does not repeat'}</option>
                <option value="daily">{t('daily') || 'Daily'}</option>
                <option value="weekly">{t('weekly') || 'Weekly'}</option>
                <option value="monthly">{t('monthly') || 'Monthly'}</option>
              </select>
            </div>

            {formData.recurrence_type !== 'none' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('recurrenceEndDate') || 'Ends on'}
                </label>
                <input
                  type="date"
                  name="recurrence_end_date"
                  value={formData.recurrence_end_date}
                  onChange={handleChange}
                  required={formData.recurrence_type !== 'none'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('recurrenceEndDateHelp') || 'Last date for recurring events'}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>
              {t('cancel')}
            </Button>
            <Button type="submit">
              {editingEvent ? t('save') : t('create')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Assigned Leads Modal */}
      <Modal
        isOpen={leadsModalOpen}
        onClose={handleCloseLeadsModal}
        title={`${t('assignedLeads') || 'Assigned Leads'} - ${selectedEventName}`}
      >
        <div className="mb-4">
          <Button onClick={handleOpenAddLeadModal} variant="primary">
            {t('addLead') || 'Add Lead'}
          </Button>
        </div>
        {loadingLeads ? (
          <div className="text-center py-8">{t('loading')}</div>
        ) : selectedEventLeads.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('noLeadsAssigned') || 'No leads assigned to this event'}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Statistics Summary */}
            {(() => {
              const confirmedLeads = selectedEventLeads.filter(lead =>
                (attendanceStatus[lead.id] || lead.attendance_status) !== 'waiting_list'
              );
              const waitingListLeads = selectedEventLeads.filter(lead =>
                (attendanceStatus[lead.id] || lead.attendance_status) === 'waiting_list'
              );
              const totalAssigned = confirmedLeads.length;
              const capacity = selectedEvent?.capacity || 0;
              const attended = confirmedLeads.filter(lead =>
                (attendanceStatus[lead.id] || lead.attendance_status) === 'attended'
              ).length;
              const didntArrive = confirmedLeads.filter(lead => {
                const status = attendanceStatus[lead.id] || lead.attendance_status;
                return status === 'absent' || status === 'no_show';
              }).length;
              const missingHealthDeclaration = confirmedLeads.filter(lead =>
                !healthDeclarations[lead.id] && !lead.health_declaration
              ).length;

              return (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="text-xs text-blue-600 font-medium mb-1">
                        {t('assignedOfCapacity') || 'Assigned / Capacity'}
                      </div>
                      <div className="text-2xl font-bold text-blue-900">
                        {totalAssigned}{capacity > 0 ? ` / ${capacity}` : ''}
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="text-xs text-green-600 font-medium mb-1">
                        {t('attended') || 'Attended'}
                      </div>
                      <div className="text-2xl font-bold text-green-900">{attended}</div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="text-xs text-red-600 font-medium mb-1">
                        {t('didntArrive') || "Didn't Arrive"}
                      </div>
                      <div className="text-2xl font-bold text-red-900">{didntArrive}</div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <div className="text-xs text-orange-600 font-medium mb-1">
                        {t('missingHealthDeclaration') || 'Missing Health Statement'}
                      </div>
                      <div className="text-2xl font-bold text-orange-900">{missingHealthDeclaration}</div>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="text-xs text-purple-600 font-medium mb-1">
                        {t('waitingList') || 'Waiting List'}
                      </div>
                      <div className="text-2xl font-bold text-purple-900">{waitingListLeads.length}</div>
                    </div>
                  </div>

                  {/* Confirmed Leads Section */}
                  {confirmedLeads.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        {t('confirmedAttendees') || 'Confirmed Attendees'}
                      </h4>
                    </div>
                  )}
                </>
              );
            })()}
            {/* Desktop Table View */}
            <div className="hidden md:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('name') || 'Name'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('phone') || 'Phone'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('group') || 'Group'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('attendance') || 'Attendance'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('healthDeclaration') || 'Health Declaration'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('actions') || 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedEventLeads.filter(lead =>
                      (attendanceStatus[lead.id] || lead.attendance_status) !== 'waiting_list'
                    ).map((lead) => {
                      const currentAttendanceStatus = attendanceStatus[lead.id] || lead.attendance_status;
                      const isAttended = currentAttendanceStatus === 'attended';
                      const isAbsent = currentAttendanceStatus === 'absent';
                      const hasHealthDeclaration = healthDeclarations[lead.id] === true;

                      return (
                      <tr key={lead.id}>
                        <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <div className="flex items-center gap-2">
                            <span>{lead.field_data?.full_name || '-'}</span>
                            {!hasHealthDeclaration && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                                title={t('missingHealthDeclaration') || 'Missing health declaration'}
                              >
                                ⚠️
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {lead.field_data?.phone || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {lead.group_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <div className="flex items-center justify-center gap-3">
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                key={`attended-${lead.id}`}
                                type="radio"
                                name={`attendance-${lead.id}`}
                                value="attended"
                                className="w-5 h-5 text-green-600 border-2 border-gray-300 focus:ring-green-500 focus:ring-2"
                                checked={isAttended}
                                onChange={() => handleAttendanceChange(lead.id, 'attended')}
                                style={{
                                  accentColor: '#16a34a'
                                }}
                              />
                              <span className={`text-sm font-medium ${isAttended ? 'text-green-700' : 'text-gray-600'}`}>
                                {t('attended') || 'הגיע'}
                              </span>
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                key={`absent-${lead.id}`}
                                type="radio"
                                name={`attendance-${lead.id}`}
                                value="absent"
                                className="w-5 h-5 text-red-600 border-2 border-gray-300 focus:ring-red-500 focus:ring-2"
                                checked={isAbsent}
                                onChange={() => handleAttendanceChange(lead.id, 'absent')}
                                style={{
                                  accentColor: '#dc2626'
                                }}
                              />
                              <span className={`text-sm font-medium ${isAbsent ? 'text-red-700' : 'text-gray-600'}`}>
                                {t('absent') || 'לא הגיע'}
                              </span>
                            </label>
                            {(isAttended || isAbsent) && (
                              <button
                                onClick={() => handleAttendanceChange(lead.id, 'pending')}
                                className="ml-1 text-gray-400 hover:text-gray-600 transition-colors"
                                title={t('resetAttendance') || 'Reset attendance'}
                              >
                                <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                  <path d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleHealthDeclarationChange(lead.id, !hasHealthDeclaration)}
                              className={`flex items-center justify-center w-8 h-8 rounded-full transition-all cursor-pointer hover:scale-110 ${
                                hasHealthDeclaration 
                                  ? 'bg-green-100 border-2 border-green-500 hover:bg-green-200' 
                                  : 'bg-gray-100 border-2 border-gray-300 hover:bg-gray-200'
                              }`}
                              title={hasHealthDeclaration ? (t('healthDeclarationReceived') || 'Health declaration received - Click to cancel') : (t('healthDeclarationNotReceived') || 'No health declaration - Click to mark')}
                            >
                              {hasHealthDeclaration ? (
                                <svg className="w-5 h-5 text-green-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                  <path d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                  <path d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <button
                            onClick={() => handleUnassignLead(lead.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('unassign') || 'Unassign'}
                          >
                            <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {(() => {
                const confirmedLeads = selectedEventLeads.filter(lead =>
                  (attendanceStatus[lead.id] || lead.attendance_status) !== 'waiting_list'
                );
                const pendingLeads = confirmedLeads.filter(lead => {
                  const status = attendanceStatus[lead.id] || lead.attendance_status;
                  return !status || status === 'pending';
                });
                const markedLeads = confirmedLeads.filter(lead => {
                  const status = attendanceStatus[lead.id] || lead.attendance_status;
                  return status === 'attended' || status === 'absent' || status === 'no_show';
                });

                const renderMobileCard = (lead) => {
                  const currentAttendanceStatus = attendanceStatus[lead.id] || lead.attendance_status;
                  const isAttended = currentAttendanceStatus === 'attended';
                  const isAbsent = currentAttendanceStatus === 'absent';
                  const hasHealthDeclaration = healthDeclarations[lead.id] === true;

                  return (
                    <div key={lead.id} className="bg-gray-50 rounded-lg p-3 border relative">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 text-right flex-wrap">
                            <h4 className="text-sm font-semibold text-gray-900">{lead.field_data?.full_name || '-'}</h4>
                            <div className="relative flex-shrink-0">
                              <button
                                onClick={() => handlePhoneClick(lead.id, lead.field_data?.phone)}
                                className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                              >
                                {lead.field_data?.phone || '-'}
                              </button>
                              {phoneMenuOpen === lead.id && lead.field_data?.phone && (
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
                                  <div className="py-1">
                                    <button
                                      onClick={() => handleCopyPhone(lead.field_data.phone)}
                                      className="block w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      {t('copyToClipboard')}
                                    </button>
                                    <button
                                      onClick={() => handleOpenWhatsApp(lead.field_data.phone)}
                                      className="block w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      {t('openInWhatsApp')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {!hasHealthDeclaration && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0"
                                title={t('missingHealthDeclaration') || 'Missing health declaration'}
                              >
                                ⚠️
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleHealthDeclarationChange(lead.id, !hasHealthDeclaration)}
                            className={`flex items-center justify-center w-5 h-5 rounded-full transition-all ${
                              hasHealthDeclaration
                                ? 'bg-green-100 border border-green-500'
                                : 'bg-gray-100 border border-gray-300'
                            }`}
                            title={hasHealthDeclaration ? t('healthDeclarationReceived') : t('healthDeclarationNotReceived')}
                          >
                            {hasHealthDeclaration ? (
                              <svg className="w-3 h-3 text-green-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-3 h-3 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => handleUnassignLead(lead.id)}
                            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded"
                            title={t('unassign') || 'Unassign'}
                          >
                            <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-center gap-2 pt-2 border-t border-gray-200">
                        <button
                          onClick={() => handleAttendanceChange(lead.id, 'attended')}
                          className={`flex-1 py-1.5 px-3 rounded text-sm font-medium transition-colors ${
                            isAttended
                              ? 'bg-green-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {t('attended') || 'הגיע'}
                        </button>
                        <button
                          onClick={() => handleAttendanceChange(lead.id, 'absent')}
                          className={`flex-1 py-1.5 px-3 rounded text-sm font-medium transition-colors ${
                            isAbsent
                              ? 'bg-red-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {t('absent') || 'לא הגיע'}
                        </button>
                        {(isAttended || isAbsent) && (
                          <button
                            onClick={() => handleAttendanceChange(lead.id, 'pending')}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title={t('resetAttendance') || 'Reset attendance'}
                          >
                            <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                };

                return (
                  <>
                    {/* Pending Attendance Section */}
                    {pendingLeads.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-2 px-1">
                          {t('pendingAttendance') || 'טרם סומן'} ({pendingLeads.length})
                        </h5>
                        <div className="space-y-2">
                          {pendingLeads.map(renderMobileCard)}
                        </div>
                      </div>
                    )}

                    {/* Marked Attendance Section */}
                    {markedLeads.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-2 px-1">
                          {t('markedAttendance') || 'סומנו'} ({markedLeads.length})
                        </h5>
                        <div className="space-y-2">
                          {markedLeads.map(renderMobileCard)}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Waiting List Section */}
            {(() => {
              const waitingListLeads = selectedEventLeads.filter(lead =>
                (attendanceStatus[lead.id] || lead.attendance_status) === 'waiting_list'
              );

              if (waitingListLeads.length === 0) return null;

              return (
                <div className="mt-8 pt-6 border-t-2 border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-900 mb-4">
                    {t('waitingList') || 'Waiting List'} ({waitingListLeads.length})
                  </h4>

                  {/* Desktop Waiting List Table */}
                  <div className="hidden md:block">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-purple-50">
                          <tr>
                            <th className={`px-4 py-3 text-xs font-medium text-purple-700 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                              {t('name') || 'Name'}
                            </th>
                            <th className={`px-4 py-3 text-xs font-medium text-purple-700 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                              {t('phone') || 'Phone'}
                            </th>
                            <th className={`px-4 py-3 text-xs font-medium text-purple-700 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                              {t('group') || 'Group'}
                            </th>
                            <th className={`px-4 py-3 text-xs font-medium text-purple-700 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                              {t('actions') || 'Actions'}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {waitingListLeads.map((lead) => (
                            <tr key={lead.id} className="bg-purple-50/30">
                              <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                                {lead.field_data?.full_name || '-'}
                              </td>
                              <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                                {lead.field_data?.phone || '-'}
                              </td>
                              <td className={`px-4 py-3 text-sm text-gray-600 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                                {lead.group_name || '-'}
                              </td>
                              <td className={`px-4 py-3 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleConfirmFromWaitingList(lead.id)}
                                    className="px-3 py-1 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors"
                                  >
                                    {t('confirm') || 'Confirm'}
                                  </button>
                                  <button
                                    onClick={() => handleUnassignLead(lead.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Waiting List Cards */}
                  <div className="md:hidden space-y-3">
                    {waitingListLeads.map((lead) => (
                      <div key={lead.id} className="bg-purple-50/50 rounded-lg p-4 border border-purple-200">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="font-medium text-gray-900">{lead.field_data?.full_name || '-'}</div>
                            <div className="text-sm text-gray-600 mt-1">{lead.field_data?.phone || '-'}</div>
                            {lead.group_name && (
                              <div className="text-xs text-gray-500 mt-1">
                                {t('group')}: {lead.group_name}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 pt-3 border-t border-purple-200">
                          <button
                            onClick={() => handleConfirmFromWaitingList(lead.id)}
                            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors"
                          >
                            {t('confirm') || 'Confirm'}
                          </button>
                          <button
                            onClick={() => handleUnassignLead(lead.id)}
                            className="px-3 py-2 text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-between items-center pt-4 border-t">
              <Button 
                variant="primary" 
                onClick={handleCopyParticipantsList}
                disabled={selectedEventLeads.length === 0}
              >
                <svg className="w-4 h-4 mr-2" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {t('copyParticipantsList') || 'Copy Participants List'}
              </Button>
              <Button variant="secondary" onClick={handleCloseLeadsModal}>
                {t('close')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Assign Group to Event Modal */}
      <Modal
        isOpen={groupAssignModalOpen}
        onClose={handleCloseGroupAssignModal}
        title={`${t('assignGroup') || 'Assign Group'} - ${selectedEvent?.event_name || ''}`}
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              {t('assignGroupInfo') || 'Select a group to assign all its members to this event.'}
            </p>
            {selectedEvent && (
              <p className="text-xs text-blue-600 mt-1">
                {t('currentCapacity') || 'Current capacity'}: {selectedEvent.assigned_count || 0}
                {selectedEvent.capacity > 0 ? ` / ${selectedEvent.capacity}` : ` (${t('unlimited') || 'Unlimited'})`}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('selectGroup') || 'Select Group'}
            </label>
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('selectGroup') || 'Select a group'}</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.group_name} ({group.member_count || 0} {t('members') || 'members'})
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseGroupAssignModal}>
              {t('cancel')}
            </Button>
            <Button type="button" onClick={handleAssignGroup}>
              {t('assign') || 'Assign'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Lead to Event Modal */}
      <Modal
        isOpen={addLeadModalOpen}
        onClose={handleCloseAddLeadModal}
        title={`${t('addLeadToEvent') || 'Add Lead to Event'} - ${selectedEvent?.event_name || ''}`}
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              {t('addLeadToEventInfo') || 'Select a lead to add to this event.'}
            </p>
          </div>

          {loadingAvailableLeads ? (
            <div className="text-center py-8">{t('loading')}</div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('searchLead') || 'Search Lead'}
                </label>
                <Input
                  type="text"
                  value={leadSearchQuery}
                  onChange={(e) => setLeadSearchQuery(e.target.value)}
                  placeholder={t('searchByNameOrPhone') || 'Search by name or phone...'}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('selectLead') || 'Select Lead'}
                </label>
                <select
                  value={selectedLeadToAdd}
                  onChange={(e) => setSelectedLeadToAdd(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('selectLead') || 'Select Lead'}</option>
                  {availableLeads
                    .filter((lead) => {
                      if (!leadSearchQuery) return true;
                      const fullName = lead.field_data?.full_name?.toLowerCase() || '';
                      const phone = lead.field_data?.phone?.toLowerCase() || '';
                      const query = leadSearchQuery.toLowerCase();
                      return fullName.includes(query) || phone.includes(query);
                    })
                    .map((lead) => (
                      <option key={lead.id} value={lead.id}>
                        {lead.field_data?.full_name || 'Unnamed'} {lead.field_data?.phone ? `- ${lead.field_data.phone}` : ''}
                      </option>
                    ))}
                </select>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseAddLeadModal}>
              {t('cancel')}
            </Button>
            <Button type="button" onClick={handleAddLeadToEvent} disabled={!selectedLeadToAdd}>
              {t('add') || 'Add'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
